import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import type { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import { useNurturingTestAutomationProgress } from 'app/hooks/useNurturingTestAutomationProgress';
import HumanInTheLoopApiService from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';
import type { AutomationNeedingAttentionResolveBody } from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  extractJobIdsFromResolveBody,
  getResolveProgressTotal,
  isNurturingAutomationHttpSuccess,
} from 'app/utils/nurturingTestAutomationProgressUtils';
import {
  findSegmentAttentionRowInQueryCache,
  resolveGridRowFromCellParams,
} from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/utils';
import {
  extractSegmentNameFromSegmentRow,
  getSegmentAttentionGridRowKey,
  getSegmentAttentionImpactedCount,
  getSegmentAttentionReasonCode,
  HIL_SEGMENTS_OPEN_RESOLVE_ACTIONS,
  HIL_SEGMENTS_RETRY,
} from 'app/utils/hilSegmentsAttentionUtils';

const HIL_TEST_OPPORTUNITY_TYPE = 'test';
const HIL_SEGMENTS_ATTENTION_ENDPOINT =
  'api/v2/nurturing-automation/automation-needing-attention';

export type UseHilSegmentAttentionResolveOptions = {
  dealerId: string;
  canAccess: boolean;
  onResolveComplete?: () => void;
};

export function useHilSegmentAttentionResolve(
  options: UseHilSegmentAttentionResolveOptions,
) {
  const { dealerId, canAccess, onResolveComplete } = options;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const dataGridRef = useRef<DataGridMAIQRef | null>(null);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveModalRow, setResolveModalRow] = useState<unknown>(null);
  const [resolveApiResponseBody, setResolveApiResponseBody] =
    useState<AutomationNeedingAttentionResolveBody | null>(null);
  const [resolveResponseVersion, setResolveResponseVersion] = useState(0);
  const [showResolveProgress, setShowResolveProgress] = useState(false);
  const [resolveActionLoading, setResolveActionLoading] = useState(false);

  const automation = useNurturingTestAutomationProgress({
    dealerId,
    canAccess,
    enablePersistence: false,
    progressTitle: 'Resolving segment issues...',
    invalidatePrefixesOnComplete: [
      {
        prefix: HIL_SEGMENTS_ATTENTION_ENDPOINT,
        method: 'GET',
      },
    ],
    onComplete: () => {
      void queryClient.invalidateQueries({
        queryKey: ['hil-segments-attention-count', dealerId],
      });
      onResolveComplete?.();
    },
  });

  const setRowRetrying = useCallback((row: unknown, retrying: boolean) => {
    const rowKey = getSegmentAttentionGridRowKey(row);
    if (!rowKey) return;
    dataGridRef.current?.manualUpdateData(rowKey, { __retrying: retrying });
  }, []);

  const openResolveModal = useCallback(
    (cellParams: unknown) => {
      const clickedRow = resolveGridRowFromCellParams(cellParams);
      const freshestRow = findSegmentAttentionRowInQueryCache(
        queryClient,
        clickedRow,
      );
      setResolveModalRow(freshestRow);
      setResolveModalOpen(true);
    },
    [queryClient],
  );

  const closeResolveModal = useCallback(() => {
    setResolveModalOpen(false);
    setResolveModalRow(null);
  }, []);

  const resolveRow = useCallback(
    async (row: unknown) => {
      const segmentName = extractSegmentNameFromSegmentRow(row);
      const reasonCode = getSegmentAttentionReasonCode(row);
      if (!dealerId.trim() || !segmentName || !reasonCode) return;

      setResolveActionLoading(true);
      setRowRetrying(row, true);
      try {
        const response =
          await HumanInTheLoopApiService.postAutomationNeedingAttentionResolve({
            dealer_id: dealerId,
            segment_name: segmentName,
            reason_code: reasonCode,
            opportunity_type: HIL_TEST_OPPORTUNITY_TYPE,
          });

        if (!isNurturingAutomationHttpSuccess(response.statusCode)) {
          const msg = response.message ? String(response.message) : '';
          if (msg) {
            dispatch(
              notificationAlertActions.addNotification({
                open: true,
                severity: 'error',
                message: msg,
              }),
            );
          }
          return;
        }

        const body = response.body ?? null;
        setResolveApiResponseBody(body);
        setResolveResponseVersion(prev => prev + 1);

        const reruns = Array.isArray(body?.reruns) ? body.reruns : [];
        const hasReruns = reruns.length > 0;
        const jobIds = extractJobIdsFromResolveBody(body);

        if (hasReruns && jobIds.length > 0) {
          const progressTotal = getResolveProgressTotal(
            body,
            getSegmentAttentionImpactedCount(row),
          );
          automation.startSession({
            jobIds,
            progressTotal,
            primaryJobId: jobIds[0],
          });
          setShowResolveProgress(true);
        } else {
          automation.resetSession();
          setShowResolveProgress(false);
        }

        const successMessage = response.message ? String(response.message) : '';
        if (successMessage) {
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'success',
              message: successMessage,
            }),
          );
        }

        // If resolve actions modal is open for this row, close it on success.
        setResolveModalOpen(false);
        setResolveModalRow(null);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : '';
        if (msg) {
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'error',
              message: msg,
            }),
          );
        }
      } finally {
        setRowRetrying(row, false);
        setResolveActionLoading(false);
      }
    },
    [automation, dealerId, dispatch, setRowRetrying],
  );

  const handleCellClick = useCallback(
    (params: { row?: unknown }, clickedElement?: string) => {
      if (clickedElement === HIL_SEGMENTS_RETRY) {
        void resolveRow(params?.row);
        return;
      }

      if (clickedElement === HIL_SEGMENTS_OPEN_RESOLVE_ACTIONS) {
        openResolveModal(params);
      }
    },
    [openResolveModal, resolveRow],
  );

  const handleResolveModalSuccess = useCallback(() => {
    onResolveComplete?.();
  }, [onResolveComplete]);

  return {
    dataGridRef,
    handleCellClick,
    resolveRow,
    resolveModalOpen,
    resolveModalRow,
    closeResolveModal,
    handleResolveModalSuccess,
    resolveApiResponseBody,
    resolveResponseVersion,
    showResolveProgress,
    resolveActionLoading,
    ...automation,
  };
}
